// utils/api/config.ts
import { logoutCms } from "../../store/cmsAuth";
import type { DeleteOptions, ApiResponse } from "../../types/cms/login";
import { getAuthToken } from "../cms/auth";

// Generic fetch function for non-CMS endpoints
export async function fetchData(
  endpoint: string,
  params?: Record<string, string | number>,
  method: "GET" | "POST" | "DELETE" = "GET",
  body?: Record<string, unknown>,
  withCredentials: boolean = false,
) {
  const baseUrl = import.meta.env.PUBLIC_BACKEND_BASE_URL;
  const url = new URL(`${baseUrl}/${endpoint}`);

  if (params && method === "GET") {
    Object.keys(params).forEach((key) =>
      url.searchParams.append(key, String(params[key])),
    );
  }

  try {
    const response = await fetch(url.toString(), {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      body:
        method === "POST" || method === "DELETE" ? JSON.stringify(body) : null,
      credentials: withCredentials ? "include" : "same-origin", // Handle cookies if needed
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} - ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Failed to fetch data:", error);
    throw new Error(
      error instanceof Error ? error.message : "An unknown error occurred",
    );
  }
}

// Universities data fetch for non-CMS endpoints
export async function fetchUniversitiesData(
  endpoint: string,
  params?: Record<string, string | number>,
) {
  const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
  const url = new URL(`${baseUrl}/${endpoint}`);

  if (params) {
    Object.keys(params).forEach((key) =>
      url.searchParams.append(key, String(params[key])),
    );
  }

  try {
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error(`Error: ${response.status} - ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch data:", error);
    return null;
  }
}

// Login POST request
export async function postRequest<T>(
  endpoint: string,
  params: Record<string, string>,
): Promise<ApiResponse<T>> {
  const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
  const url = new URL(`${baseUrl}/${endpoint}`);

  try {
    const response = await fetch(url.toString(), {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams(params).toString(),
    });

    if (!response.ok) {
      throw new Error(`${response.status} - ${response.statusText}`);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    return {
      error:
        error instanceof Error ? error.message : "An unknown error occurred",
    };
  }
}

// Protected CMS data fetch
export async function cmsFetchData<T>(
  endpoint: string,
  params?: Record<string, string | number>,
): Promise<T | null> {
  const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
  const url = new URL(`${baseUrl}/${endpoint}`);
  const access_token = getAuthToken();

  if (!access_token) {
    console.error("No access token available");
    return null;
  }

  if (params) {
    Object.keys(params).forEach((key) =>
      url.searchParams.append(key, String(params[key])),
    );
  }

  try {
    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${access_token}`,
      },
    });

    if (response.status === 401) {
      logoutCms();
      window.location.href = "/login";
      return null;
    }

    if (!response.ok) {
      throw new Error(`Error: ${response.status} - ${response.statusText}`);
    }
    const data = await response.json();
    return data as T;
  } catch (error) {
    console.error("Failed to fetch data:", error);
    return null;
  }
}

// Delete single university
export const deleteUniversity = async (endpoint: string) => {
  const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
  const url = new URL(`${baseUrl}/${endpoint}`);
  const access_token = getAuthToken();

  if (!access_token) {
    throw new Error("No access token available");
  }

  try {
    const response = await fetch(url.toString(), {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access_token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to delete university: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Delete API Error:", error);
    throw error;
  }
};

// Delete multiple universities
export const deleteMultiUniversity = async <T>(
  endpoint: string,
  options: DeleteOptions = {},
): Promise<T> => {
  const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
  const url = new URL(`${baseUrl}/${endpoint}`);
  const access_token = getAuthToken();

  if (!access_token) {
    throw new Error("No access token available");
  }

  try {
    const response = await fetch(url.toString(), {
      method: options.method || "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access_token}`,
      },
      ...(options.body && { body: JSON.stringify(options.body) }),
    });

    if (!response.ok) {
      throw new Error(`Delete request failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Delete API Error:", error);
    throw error;
  }
};

// Update university data
export const putCmsData = async <T>(endpoint: string, data: T) => {
  const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
  const url = new URL(`${baseUrl}/${endpoint}`);
  const access_token = getAuthToken();

  if (!access_token) {
    throw new Error("No access token available");
  }

  try {
    const response = await fetch(url.toString(), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${access_token}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`Failed to update university: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Update API Error:", error);
    throw error;
  }
};

// Upload image
export async function cmsUploadImage(
  endpoint: string,
  file: File,
  params?: Record<string, string | number>,
): Promise<string> {
  const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
  const url = new URL(`${baseUrl}/${endpoint}`);
  const access_token = getAuthToken();

  if (!access_token) {
    throw new Error("No access token available");
  }

  const formData = new FormData();
  formData.append("file", file);

  if (params) {
    Object.keys(params).forEach((key) =>
      url.searchParams.append(key, String(params[key])),
    );
  }

  try {
    const response = await fetch(url.toString(), {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${access_token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        `Upload failed: ${response.status} - ${errorData?.detail?.[0]?.msg || response.statusText}`,
      );
    }

    const data = await response.json();
    return data.file_url;
  } catch (error) {
    console.error("Failed to upload image:", error);
    throw error;
  }
}

// Fetch single university
export async function fetchSingleUniversity<T>(
  endpoint: string,
  params?: Record<string, string | number>,
): Promise<T | null> {
  const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
  const url = new URL(`${baseUrl}/${endpoint}`);
  const access_token = getAuthToken();

  if (!access_token) {
    console.error("No access token available");
    return null;
  }

  if (params) {
    Object.keys(params).forEach((key) =>
      url.searchParams.append(key, String(params[key])),
    );
  }

  try {
    const response = await fetch(url.toString(), {
      method: "GET",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${access_token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.status} - ${response.statusText}`);
    }
    const data = await response.json();
    return data as T;
  } catch (error) {
    console.error("Failed to fetch university:", error);
    return null;
  }
}

// import type { DeleteOptions, ApiResponse } from "../../types/cms/login";
// import { getAuthToken } from "../cms/auth";
// import type { Token } from "../../types/cms/login";

// // CMS Config starts from here;
// const token = getAuthToken();

// export async function fetchData(
//   endpoint: string,
//   params?: Record<string, string | number>,
// ) {
//   const baseUrl = import.meta.env.PUBLIC_BACKEND_BASE_URL;
//   const url = new URL(`${baseUrl}/${endpoint}`);

//   if (params) {
//     Object.keys(params).forEach((key) =>
//       url.searchParams.append(key, String(params[key])),
//     );
//   }

//   try {
//     const response = await fetch(url.toString());
//     if (!response.ok) {
//       throw new Error(`Error: ${response.status} - ${response.statusText}`);
//     }
//     const data = await response.json();
//     return data;
//   } catch (error) {
//     console.error("Failed to fetch data:", error);
//     return null;
//   }
// }

// export async function fetchUniversitiesData(
//   endpoint: string,
//   params?: Record<string, string | number>,
// ) {
//   const baseUrl = import.meta.env.PUBLIC_UNIVERSITY_BACKEND_URL;
//   const url = new URL(`${baseUrl}/${endpoint}`);

//   if (params) {
//     Object.keys(params).forEach((key) =>
//       url.searchParams.append(key, String(params[key])),
//     );
//   }

//   try {
//     const response = await fetch(url.toString());
//     if (!response.ok) {
//       throw new Error(`Error: ${response.status} - ${response.statusText}`);
//     }
//     const data = await response.json();
//     return data;
//   } catch (error) {
//     console.error("Failed to fetch data:", error);
//     return null;
//   }
// }

// export async function postRequest<T>(
//   endpoint: string,
//   params: Record<string, string>,
// ): Promise<ApiResponse<T>> {
//   const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
//   // const url = new URL(endpoint, baseUrl);
//   const url = new URL(`${baseUrl}/${endpoint}`);

//   try {
//     const response = await fetch(url.toString(), {
//       method: "POST",
//       headers: {
//         Accept: "application/json",
//         "Content-Type": "application/x-www-form-urlencoded",
//       },
//       body: new URLSearchParams(params).toString(),
//     });

//     if (!response.ok) {
//       throw new Error(`${response.status} - ${response.statusText}`);
//     }

//     const data = await response.json();
//     return { data };
//   } catch (error) {
//     return {
//       error:
//         error instanceof Error ? error.message : "An unknown error occurred",
//     };
//   }
// }

// export async function cmsFetchData<T>(
//   endpoint: string,
//   params?: Record<string, string | number>,
// ): Promise<T | null> {
//   const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
//   const url = new URL(`${baseUrl}/${endpoint}`);
//   const parsedToken = JSON.parse(token || "");
//   const { access_token } = parsedToken as Token;

//   if (params) {
//     Object.keys(params).forEach((key) =>
//       url.searchParams.append(key, String(params[key])),
//     );
//   }

//   try {
//     const response = await fetch(url.toString(), {
//       method: "GET",
//       headers: {
//         accept: "application/json",
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${access_token}`,
//       },
//     });

//     if (!response.ok) {
//       throw new Error(`Error: ${response.status} - ${response.statusText}`);
//     }
//     const data = await response.json();
//     return data as T;
//   } catch (error) {
//     console.error("Failed to fetch data:", error);
//     return null;
//   }
// }

// export const deleteUniversity = async (endpoint: string) => {
//   const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
//   const url = new URL(`${baseUrl}/${endpoint}`);

//   try {
//     const parsedToken = JSON.parse(token || "");
//     const { access_token } = parsedToken as Token;
//     const response = await fetch(url.toString(), {
//       method: "DELETE",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${access_token}`, // Add your token here
//       },
//     });

//     if (!response.ok) {
//       throw new Error(`Failed to delete university: ${response.statusText}`);
//     }

//     return await response.json();
//   } catch (error) {
//     console.error("Delete API Error:", error);
//     throw error;
//   }
// };

// export const deleteMultiUniversity = async <T>(
//   endpoint: string,
//   options: DeleteOptions = {},
// ): Promise<T> => {
//   const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
//   const url = new URL(`${baseUrl}/${endpoint}`);

//   try {
//     const parsedToken = JSON.parse(token || "");
//     const { access_token } = parsedToken as Token;
//     const response = await fetch(url.toString(), {
//       method: options.method || "DELETE",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${access_token}`,
//       },
//       ...(options.body && { body: JSON.stringify(options.body) }),
//     });

//     if (!response.ok) {
//       throw new Error(`Delete request failed: ${response.statusText}`);
//     }

//     return await response.json();
//   } catch (error) {
//     console.error("Delete API Error:", error);
//     throw error;
//   }
// };

// export const putCmsData = async <T>(endpoint: string, data: T) => {
//   const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
//   const url = new URL(`${baseUrl}/${endpoint}`);
//   console.log(data, "data in config");
//   try {
//     const parsedToken = JSON.parse(token || "");
//     const { access_token } = parsedToken as Token;
//     const response = await fetch(url.toString(), {
//       method: "PUT", // Use PUT method
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${access_token}`, // Include the Bearer token
//       },
//       body: JSON.stringify(data), // Convert data to JSON
//     });

//     if (!response.ok) {
//       throw new Error(`Failed to update university: ${response.statusText}`);
//     }

//     return await response.json();
//   } catch (error) {
//     console.error("Update API Error:", error);
//     throw error;
//   }
// };

// // Specific image upload POST service
// export async function cmsUploadImage(
//   endpoint: string,
//   file: File,
//   params?: Record<string, string | number>,
// ): Promise<string> {
//   // Changed return type to string since we expect file_url
//   const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
//   const url = new URL(`${baseUrl}/${endpoint}`);
//   const parsedToken = JSON.parse(token || "");
//   const { access_token } = parsedToken as Token;

//   const formData = new FormData();
//   formData.append("file", file); // Using "file" as the field name per API docs

//   if (params) {
//     Object.keys(params).forEach((key) =>
//       url.searchParams.append(key, String(params[key])),
//     );
//   }

//   try {
//     const response = await fetch(url.toString(), {
//       method: "POST",
//       headers: {
//         Accept: "application/json",
//         Authorization: `Bearer ${access_token}`,
//         // Don't set Content-Type - browser will set it for multipart/form-data
//       },
//       body: formData,
//     });

//     if (!response.ok) {
//       const errorData = await response.json().catch(() => null);
//       throw new Error(
//         `Upload failed: ${response.status} - ${errorData?.detail?.[0]?.msg || response.statusText}`,
//       );
//     }

//     const data = await response.json();
//     return data.file_url; // Return the file_url from the response
//   } catch (error) {
//     console.error("Failed to upload image:", error);
//     throw error;
//   }
// }

// export async function fetchSingleUniversity<T>(
//   endpoint: string,
//   params?: Record<string, string | number>,
// ): Promise<T | null> {
//   const baseUrl = import.meta.env.PUBLIC_BACKEND_CMS_BASE_URL;
//   const url = new URL(`${baseUrl}/${endpoint}`);
//   const parsedToken = JSON.parse(token || "");
//   const { access_token } = parsedToken as Token;

//   if (params) {
//     Object.keys(params).forEach((key) =>
//       url.searchParams.append(key, String(params[key])),
//     );
//   }

//   try {
//     const response = await fetch(url.toString(), {
//       method: "GET",
//       headers: {
//         accept: "application/json",
//         "Content-Type": "application/json",
//         Authorization: `Bearer ${access_token}`,
//       },
//     });

//     if (!response.ok) {
//       throw new Error(`Error: ${response.status} - ${response.statusText}`);
//     }
//     const data = await response.json();
//     return data as T;
//   } catch (error) {
//     console.error("Failed to fetch university:", error);
//     return null;
//   }
// }
